import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, type FormHTMLAttributes } from "react";
import { useLocale } from "../i18n";
import { createFormValidation } from "./formValidation";
import "./form-validation.css";
import "./field-requirement.css";

type Props = FormHTMLAttributes<HTMLFormElement> & {
  valid?: boolean;
  validationMessage?: string;
  pending?: boolean;
};

/** Native constraints and the screen's domain guard both run before its submit handler. */
export const ValidatedForm = forwardRef<HTMLFormElement, Props>(function ValidatedForm(
  { children, onSubmit, onReset, noValidate, valid = true, validationMessage, pending = false, ...props }, ref,
) {
  const { w, lang } = useLocale();
  const form = useRef<HTMLFormElement>(null);
  const latest = useRef({ valid, validationMessage, pending, w });
  latest.current = { valid, validationMessage, pending, w };
  const validation = useRef<ReturnType<typeof createFormValidation> | null>(null);
  useImperativeHandle(ref, () => form.current!, []);
  useLayoutEffect(() => {
    const instance = createFormValidation(form.current!, () => latest.current);
    validation.current = instance;
    return () => { instance.destroy(); validation.current = null; };
  }, []);
  useLayoutEffect(() => { validation.current?.refresh(); }, [children, valid, validationMessage, pending, lang]);
  return <form {...props} ref={form} noValidate onSubmit={event => {
    if (event.target !== event.currentTarget) return;
    event.stopPropagation();
    if (pending) { event.preventDefault(); return; }
    // Schema-driven event forms own their nested/array validation and focus handling.
    const bypass = noValidate || (event.nativeEvent as SubmitEvent).submitter?.hasAttribute("formnovalidate");
    if (!bypass && !validation.current?.validate()) {
      event.preventDefault(); event.stopPropagation(); return;
    }
    if (!bypass) validation.current?.reset();
    onSubmit?.(event);
  }} onReset={event => { validation.current?.reset(); onReset?.(event); }}>{children}</form>;
});
